import {
  ConnectionBaseFragment,
  SourceType,
  useActivateConnectionSettingsQuery,
  useConnectionQuery,
  useModelQuery,
  useRawViewQuery,
} from "@/apollo/types";
import { formatModelName } from "@/features/models";
import { useIntegrationsMap } from "@/integrations";

import { Source } from "../form";

type InputOptions = {
  integrationId?: string;
  connectionId?: string;
  sourceId?: string;
  sourceType?: SourceType;
};

export function useValidInputProps(inputProps: InputOptions = {}): {
  validInputProps: {
    integrationId?: string;
    connection?: ConnectionBaseFragment;
    connectionSettings?: Record<string, any>;
    source?: Source;
  };
  isLoading: boolean;
  isValidConnectionId?: boolean | undefined;
  isValidIntegrationId?: boolean | undefined;
} {
  const integrationsRegistry = useIntegrationsMap();

  const { data: rawViewData, loading: isLoadingRawView } = useRawViewQuery({
    fetchPolicy: "network-only",
    variables: {
      viewId: inputProps.sourceId || "",
    },
    skip: !inputProps.sourceId || inputProps.sourceType !== SourceType.RawView,
  });

  const { data: modelData, loading: isLoadingModel } = useModelQuery({
    fetchPolicy: "network-only",
    variables: {
      modelId: inputProps.sourceId || "",
    },
    skip: !inputProps.sourceId || inputProps.sourceType !== SourceType.Model,
  });

  let source: Source | undefined = undefined;
  if (rawViewData?.rawView) {
    source = {
      value: rawViewData.rawView.viewId,
      label: `${rawViewData.rawView.schema}.${rawViewData.rawView.sourceStream}`,
      type: SourceType.RawView,
      integrationId: rawViewData.rawView.integrationId || undefined,
    };
  }
  if (modelData?.model) {
    source = {
      value: modelData.model.id,
      label: formatModelName(modelData.model),
      type: SourceType.Model,
    };
  }

  const { data: connectionData, loading: isLoadingConnection } =
    useConnectionQuery({
      fetchPolicy: "network-only",
      variables: {
        id: inputProps.connectionId || "",
      },
      skip: !inputProps.connectionId,
    });

  const { data: connectionSettingsData, loading: isLoadingConnectionSettings } =
    useActivateConnectionSettingsQuery({
      variables: {
        input: {
          id: inputProps.connectionId || "",
        },
      },
      skip: !inputProps.connectionId,
    });

  const isLoading =
    isLoadingConnection ||
    isLoadingConnectionSettings ||
    isLoadingRawView ||
    isLoadingModel;
  if (isLoading) {
    return {
      validInputProps: {},
      isLoading,
    };
  }

  if (inputProps.connectionId) {
    const isValidConnectionId =
      !isLoadingConnection && connectionData !== undefined;
    if (connectionData && connectionSettingsData) {
      const connectionSettings =
        connectionSettingsData.getActivateSettings.connectionSettings;
      return {
        validInputProps: {
          integrationId: connectionData.connection.integrationId,
          connection: connectionData.connection,
          connectionSettings,
          source,
        },
        isLoading,
        isValidConnectionId,
      };
    } else {
      return {
        validInputProps: { source },
        isLoading,
        isValidConnectionId,
      };
    }
  }
  if (inputProps.integrationId) {
    const integration = integrationsRegistry.get(inputProps.integrationId);
    const isValidIntegrationId = integration !== undefined;
    if (isValidIntegrationId) {
      return {
        validInputProps: { integrationId: integration.id, source },
        isLoading,
        isValidIntegrationId,
      };
    } else {
      return {
        validInputProps: { source },
        isLoading,
        isValidIntegrationId,
      };
    }
  }

  return {
    validInputProps: {
      source,
    },
    isLoading: false,
  };
}
